import type {ReactElement} from 'react';
import {useContext} from 'react';
import {AutoBranding} from '@Components/auto-branding/auto-branding';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {useStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {getAllColorHexesInComponent} from '@EmailMakerReact/lib/email-maker-component-query-library';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import {openBrandKitUpdateFromColorPaletteModal, PANEL_ID} from '@Components/modals/brand-kit-update-from-color-palette-modal/brand-kit-update-from-color-palette-modal';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';
import type {EmailMakerControlProps} from '../../right-panel-registry.types';
import {DEFAULT_PALETTE_MAX_COLORS, LIGHT_COLOR_HEX_PATTERN, PALETTE_CUSTOM_STYLE_PROPERTY} from './auto-branding-control.types';

function parsePaletteJson(json: string): ColorPaletteInfo | null {
  try {
    return JSON.parse(json) as ColorPaletteInfo;
  } catch {
    return null;
  }
}

/** With no persisted palette, derive a swatch from the email's own non-light colors so the control shows the edit state rather than empty. */
function deriveDefaultPalette(hexColors: string[]): ColorPaletteInfo | null {
  const nonLightColors = hexColors.filter((color) => {
    return !LIGHT_COLOR_HEX_PATTERN.exec(color);
  });

  if (nonLightColors.length === 0) {
    return null;
  }

  const colors = [...new Set(nonLightColors)].sort().slice(0, DEFAULT_PALETTE_MAX_COLORS);

  return {
    colors: colors.map((color) => {
      return `#${color}`;
    }),
  };
}

export function AutoBrandingControl({component}: EmailMakerControlProps): ReactElement {
  const {editor, actions} = useContext(emailMakerContext);

  const rawPaletteValue = useStyleProperty(editor, component, PALETTE_CUSTOM_STYLE_PROPERTY);

  const appliedPalette: ColorPaletteInfo | null = rawPaletteValue ? parsePaletteJson(rawPaletteValue) : deriveDefaultPalette(getAllColorHexesInComponent(editor?.getWrapper()));

  function openPalettePanel(): void {
    trackGA4CustomEvent(GA4EventName.EMAILMAKER_STYLES_VISITED);
    actions.openRightPanelColorPalette();
  }

  function handlePaletteAdd(): void {
    openPalettePanel();
  }

  function handlePaletteSelect(): void {
    openPalettePanel();
  }

  function handlePaletteEdit(): void {
    if (!appliedPalette) {
      actions.openRightPanelColorPalette();
      return;
    }
    openBrandKitUpdateFromColorPaletteModal({
      currentColorPalette: appliedPalette,
      newColorPalette: appliedPalette,
      onBrandKitUpdate: actions.openRightPanelColorPalette,
      panelId: PANEL_ID,
    });
  }

  return <AutoBranding appliedPalette={appliedPalette} onPaletteAdd={handlePaletteAdd} onPaletteEdit={handlePaletteEdit} onPaletteSelect={handlePaletteSelect} />;
}
